import { all, call, fork, put, takeEvery, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import { PageActionTypes } from './types';
import {
  getSectionItems,
  getFormData,
  itemShouldHaveValue,
  isVisible,
} from '../../utils/formUtils';
import { isUndefined, parseSearch } from '../../utils/utils';
import {
  pageRequest,
  pageError,
  pageSuccess,
  updateItemSuccess,
  setSubmitButtonId,
  unsetSubmitButtonId,
  clearForms,
} from './actions';
import { Visibility, Item, Form } from '../../types/common-types';
import { ApplicationState } from '../index';
import apiRequest from '../../utils/apiRequest';
import { TAB_ID_KEY } from '../../hooks/useTabIdKey';

const getForms = (state: ApplicationState) => state.page.data.forms || [];

function updateSubmitButtonState(form: Form) {
  const isRequired = (r: boolean | undefined) => r === true;
  const isError = (
    err: boolean | undefined | null,
    value?: string | { [key: string]: string }
  ) =>
    ((err === undefined || err === null) && isUndefined(value)) || err === true;

  // Find items with errors
  const errorItem = form.sections
    .filter(sec => isVisible(sec.visibility))
    .map(i => getSectionItems(i))
    .reduce((acc, i) => acc.concat(i), [])
    .filter(
      i =>
        itemShouldHaveValue(i) &&
        isVisible(i.visibility) &&
        isRequired(i.required)
    )
    .find(i => isError(i.error, i.value));

  // Find submit button, set visibility
  form.sections
    .filter(sec => isVisible(sec.visibility))
    .forEach(sec => {
      const items = getSectionItems(sec);
      items.forEach(i => {
        if (
          i.class === 'button' &&
          i.type === 'submit' &&
          i.visibility !== 'hidden'
        ) {
          // eslint-disable-next-line no-param-reassign
          i.visibility = errorItem ? Visibility.Disabled : Visibility.Visible;
        }
      });
    });
}

function valueManager(forms: Form[], item: Item[], formId: string) {
  // forms visibility on page
  item
    .filter(i => i.visibility !== null)
    .forEach(i => {
      const findForm = forms.find(f => f.id === i.id);
      if (findForm) findForm.visibility = i.visibility;
    });

  const findForm = forms.find(i => i.id === formId);
  if (!findForm) return false;
  const form = { ...findForm };

  // form error
  const formError = item.filter(i => i.id === formId && i.errorMsg !== null)[0];
  if (formError) {
    form.errorMsg = formError.errorMsg;
  }

  // form items
  form.sections.forEach(sec => {
    const allItems = getSectionItems(sec);
    item.forEach(it => {
      //  section visibility
      if (it.id === sec.id && it.visibility) {
        sec.visibility = it.visibility; // eslint-disable-line no-param-reassign
      }
      //  item props
      allItems.forEach(i => {
        if (i.id === it.id) {
          Object.keys(it).forEach(prop => {
            if (!['id', 'class', 'row'].includes(prop)) {
              i[prop] = it[prop]; // eslint-disable-line no-param-reassign
            }
          });
        }
      });
    });
  });
  updateSubmitButtonState(form);
  return forms.map(formItem => (formItem.id === formId ? form : formItem));
}

function sendGtagEvent(search: string) {
  if (process.env.GTAG) {
    gtag('event', 'click', { event_category: 'Click', event_label: search });
  }
  if (!('gtag' in window) || !search) return;
  let args;
  try {
    const parsed = parseSearch(search);
    args = atob(parsed.gtag);
    args = JSON.parse(args).arguments;
  } catch (e) {
    args = null;
  }
  if (!args) return;
  gtag(args[0], args[1], args[2]);
}
function sendFbPixelEvent(search: string) {
  if (process.env.FBPIXEL) {
    fbq('track', search);
  }
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* handlePageRequest(action: any) {
  const { pathname, search } = action.payload.location;
  if (pathname === '/') {
    yield put(push(`/log_in${search}`));
    return;
  }
  try {
    const breakLoadPages = ['/not_found'];
    if (pathname && !breakLoadPages.includes(pathname)) {
      const page = pathname.replace('/', '');
      yield put(clearForms());
      const { code, data, sessionData } = yield call(
        apiRequest,
        'post',
        '/get',
        {
          page,
        }
      );
      if (code !== 200) return;
      // console.log("this is the search: " + page);

      yield put(pageSuccess({ data, sessionData }));
      yield call(sendGtagEvent, page);
      yield call(sendFbPixelEvent, page);
    }
  } catch (error) {
    yield put(pageError([{ message: error.message }]));
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* handleLocationChange(action: any) {
  yield put(pageRequest({ ...action.payload }));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* handleUpdateItem(action: any) {
  const { payload } = action;
  const { callback, formId, item } = payload;
  const forms = yield select(getForms);
  const newForms = valueManager(forms, item, formId);
  yield put(updateItemSuccess({ forms: newForms }));
  if (callback) yield call(callback);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* handleSubmitForm(action: any) {
  const { payload } = action;
  const { buttonId, form, buttonData = { isButton: false } } = payload;
  const isButton = buttonData;
  delete buttonData.isButton;
  const submitButtonId = yield select(state => state.page.submitButtonId);
  const COUNTRY_CODE = process.env.COUNTRY_CODE || '';
  const { pathname } = yield select(state => state.router.location);
  const page = pathname.replace('/', '');
  const { id: formId } = form;
  if (submitButtonId && isButton) return;
  if (isButton) yield put(setSubmitButtonId({ buttonId }));
  try {
    const data = yield call(getFormData, form);
    // clear form error
    yield call(handleUpdateItem, {
      payload: { formId, item: [{ id: formId, errorMsg: '' }] },
    });
    // send hidden field 'clientId'
    if (
      (COUNTRY_CODE === 'DK' || COUNTRY_CODE === 'FI') &&
      'ga' in window &&
      'getAll' in ga
    ) {
      const instance = ga.getAll()[0];
      if (instance) data.clientId = instance.get('clientId');
    }
    const extra = {} as any;
    try {
      if (COUNTRY_CODE === 'ES' && 'IGLOO' in window) {
        if (typeof window.IGLOO?.getBlackbox === 'function') {
          const bbData = window.IGLOO.getBlackbox();
          extra.blackBoxString = bbData.blackbox;
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('ES error', e);
    }

    const tabKeyId = sessionStorage.getItem(TAB_ID_KEY);

    const { data: responseData, code } = yield call(
      apiRequest,
      'post',
      '/send',
      {
        formId,
        buttonData,
        buttonId,
        page,
        data,
        extra,
        tabKeyId,
      }
    );

    if (code === 200 && responseData && responseData.changes) {
      // update items
      yield call(handleUpdateItem, {
        payload: { formId, item: responseData.changes },
      });
    }
  } catch (error) {
    yield put(pageError([{ message: error.message }]));
  } finally {
    if (isButton) yield put(unsetSubmitButtonId());
  }
}

function* handleLogout() {
  const { code } = yield call(apiRequest, 'post', '/logout', {});
  if (code === 200) {
    yield put(push('/'));
  }
}

function* watchLocationChange() {
  yield takeEvery(PageActionTypes.LOCATION_CHANGE, handleLocationChange);
}

function* watchFetchRequest() {
  yield takeEvery(PageActionTypes.PAGE_REQUEST, handlePageRequest);
}

function* watchUpdateItemRequest() {
  yield takeEvery(PageActionTypes.UPDATE_ITEM_REQUEST, handleUpdateItem);
}

function* watchSubmitFormRequest() {
  yield takeEvery(PageActionTypes.SUBMIT_FORM_REQUEST, handleSubmitForm);
}

function* watchLogoutRequest() {
  yield takeEvery(PageActionTypes.PAGE_LOGOUT_REQUEST, handleLogout);
}

function* pageSaga() {
  yield all([
    fork(watchFetchRequest),
    fork(watchUpdateItemRequest),
    fork(watchSubmitFormRequest),
    fork(watchLogoutRequest),
    fork(watchLocationChange),
  ]);
}

export default pageSaga;
